import { CATEGORIES, PRODUCTS } from "../data/catalogue";

// CRM Public Base URL
const CRM_BASE_URL =
  process.env.REACT_APP_CRM_BASE_URL ||
  process.env.REACT_APP_CRM_API_URL ||
  "https://crm.muftlo.in";

// CRM Contact Form Submission Endpoint
const CRM_CONTACT_URL =
  process.env.REACT_APP_CRM_CONTACT_URL ||
  "https://crm.muftlo.in/api/forms/submit/form-71owsdkj2";

const getTenantHeaders = () => {
  const headers = {
    Accept: "application/json",
  };
  const envTenant = process.env.REACT_APP_TENANT_DOMAIN;
  let tenantDomain =
    envTenant ||
    (typeof window !== "undefined" && window.location?.hostname ? window.location.hostname : "www.ashlarbuildchem.com");

  if (
    !tenantDomain ||
    tenantDomain === "localhost" ||
    tenantDomain === "127.0.0.1" ||
    tenantDomain === "ashlarbuildchem.com"
  ) {
    tenantDomain = "www.ashlarbuildchem.com";
  }

  if (tenantDomain) {
    headers["x-tenant-domain"] = tenantDomain;
  }
  return headers;
};

// ----------------------------------------------------
// Catalogue APIs (Client-side Standalone)
// ----------------------------------------------------

export const getCategories = async () => {
  return [...CATEGORIES];
};

export const getProducts = async (category) => {
  if (!category) return [...PRODUCTS];
  return PRODUCTS.filter((p) => p.category === category);
};

export const getProduct = async (slug) => {
  const product = PRODUCTS.find((p) => p.slug === slug);
  if (!product) return null;
  const related = PRODUCTS.filter((p) => p.category === product.category && p.slug !== slug).slice(0, 3);
  return { product, related };
};

// ----------------------------------------------------
// Blog APIs (CRM Public Integration)
// ----------------------------------------------------

export const normalizeBlogPost = (post) => {
  if (!post) return null;
  const content = post.content || post.body || "";
  const words = content ? content.trim().split(/\s+/).length : 0;
  const calculatedReadTime = words > 0 ? `${Math.max(1, Math.ceil(words / 200))} min read` : "4 min read";

  // Extract FAQs
  let rawFaqs = [];
  if (Array.isArray(post.faqs)) {
    rawFaqs = post.faqs;
  } else if (Array.isArray(post.faq)) {
    rawFaqs = post.faq;
  } else if (typeof post.faqs === "string") {
    try {
      rawFaqs = JSON.parse(post.faqs);
    } catch {}
  }

  const faqs = Array.isArray(rawFaqs)
    ? rawFaqs
        .map((f) => ({
          question: (f?.question || f?.q || f?.title || "").trim(),
          answer: (f?.answer || f?.a || f?.content || f?.description || "").trim(),
        }))
        .filter((f) => f.question && f.answer)
    : [];

  // Extract Quick Answer / TL;DR
  const quick_answer = (
    post.quick_answer ||
    post.quickAnswer ||
    post.tldr ||
    post.tl_dr ||
    post.quick_response ||
    ""
  ).trim();

  return {
    ...post,
    slug: post.slug || post.id || "",
    title: post.title || "Untitled Post",
    content,
    excerpt: post.excerpt || post.description || post.summary || (content ? content.slice(0, 160) + "..." : ""),
    image:
      post.image ||
      post.coverImage ||
      post.thumbnail ||
      post.featured_image ||
      "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?q=80&w=1600&auto=format&fit=crop",
    category: post.category || (Array.isArray(post.tags) && post.tags.length > 0 ? post.tags[0] : "Technical Guide"),
    tags: Array.isArray(post.tags) ? post.tags : post.category ? [post.category] : [],
    read_time: post.read_time || post.readTime || calculatedReadTime,
    date:
      post.date ||
      (post.createdAt ? new Date(post.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "") ||
      (post.publishedAt ? new Date(post.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Recent"),
    quick_answer,
    faqs,
    meta_title: post.metaTitle || post.meta_title || post.title,
    meta_description: post.metaDescription || post.meta_description || post.excerpt,
    meta_keywords: post.metaKeywords || post.meta_keywords || "",
  };
};

export const fetchTenantBlogs = async () => {
  try {
    const res = await fetch(`${CRM_BASE_URL}/api/public/blogs`, {
      headers: getTenantHeaders(),
    });
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data)) {
        return data.map(normalizeBlogPost);
      }
    }
  } catch (err) {
    console.warn("Failed to fetch CRM blogs:", err);
  }
  return [];
};

export const fetchBlogPost = async (slug) => {
  try {
    const res = await fetch(`${CRM_BASE_URL}/api/public/blogs/${slug}`, {
      headers: getTenantHeaders(),
    });
    if (res.ok) {
      const data = await res.json();
      if (data && (data.title || data.slug || data.content)) {
        return normalizeBlogPost(data);
      }
    }
  } catch (err) {
    console.warn(`Failed to fetch CRM blog post (${slug}):`, err);
  }
  return null;
};

export const fetchSimilarBlogs = async (slug) => {
  try {
    const res = await fetch(`${CRM_BASE_URL}/api/public/blogs/${slug}/similar`, {
      headers: getTenantHeaders(),
    });
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        return data.map(normalizeBlogPost);
      }
    }
  } catch (err) {
    console.warn(`Failed to fetch CRM similar blogs (${slug}):`, err);
  }
  try {
    const all = await fetchTenantBlogs();
    return all.filter((b) => b.slug !== slug).slice(0, 3);
  } catch {
    return [];
  }
};

export const getBlog = fetchTenantBlogs;
export const getBlogPost = fetchBlogPost;
export const getSimilarBlogs = fetchSimilarBlogs;

// CRM Career Form Endpoints
const CRM_CAREER_URL =
  process.env.REACT_APP_CRM_CAREER_URL ||
  "https://crm.muftlo.in/api/forms/submit/form-ubhu4lxoq";

const CRM_CAREER_SCHEMA_URL =
  process.env.REACT_APP_CRM_CAREER_SCHEMA_URL ||
  "https://crm.muftlo.in/api/forms/schema/form-ubhu4lxoq";

const CRM_UPLOAD_URL =
  process.env.REACT_APP_CRM_UPLOAD_URL ||
  "https://crm.muftlo.in/api/forms/upload";

// ----------------------------------------------------
// Contact Form Submission API
// ----------------------------------------------------

export const submitContact = async (formData) => {
  const payload = {
    "full-name": formData.name?.trim() || formData["full-name"]?.trim() || "",
    email: formData.email?.trim() || "",
    phone: Number(formData.phone) || (formData.phone ? Number(formData.phone.toString().replace(/\D/g, "")) : 0),
    comapny: formData.company?.trim() || formData.comapny?.trim() || "",
    subject: formData.subject?.trim() || "",
    message: formData.message?.trim() || "",
  };

  const res = await fetch(CRM_CONTACT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...getTenantHeaders(),
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    const errorMsg = errorData?.error || errorData?.message || "Failed to submit form";
    throw new Error(errorMsg);
  }

  return res.json();
};

// ----------------------------------------------------
// Career / Job Application APIs (crm.muftlo.in)
// Form ID: form-ubhu4lxoq
// ----------------------------------------------------

export const fetchCareerFormSchema = async () => {
  try {
    const res = await fetch(CRM_CAREER_SCHEMA_URL, {
      headers: {
        Accept: "application/json",
      },
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn("Failed to fetch career form schema:", err);
  }
  return null;
};

export const uploadFormFile = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(CRM_UPLOAD_URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      ...getTenantHeaders(),
    },
    body: formData,
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    const errorMsg = errorData?.error || errorData?.message || "Failed to upload resume file";
    throw new Error(errorMsg);
  }

  const data = await res.json();
  const fileUrl = data.fileUrl || data.url;
  if (!fileUrl) {
    throw new Error("Upload completed but no file URL was returned.");
  }
  return { ...data, fileUrl };
};

export const submitCareerApplication = async (formData, file = null) => {
  let resumeUrl = formData.resumeUrl?.trim() || formData["resume-link-linkedin-url"]?.trim() || formData.portfolio?.trim() || "";

  // Note: File is uploaded ONLY on submission ("don't upload file until user it submit")
  if (file) {
    const uploadRes = await uploadFormFile(file);
    resumeUrl = uploadRes.fileUrl;
  }

  const candidateName = formData.name?.trim() || formData["full-name"]?.trim() || "";
  const email = formData.email?.trim() || formData["email-address"]?.trim() || "";
  const rawPhone = formData.phone || formData["phone-number"] || "";
  const numericPhone = Number(rawPhone.toString().replace(/\D/g, "")) || 0;
  const currentOrg = formData.company?.trim() || formData["current-company-college"]?.trim() || "";
  const role = formData.position?.trim() || formData["position-applied-for"]?.trim() || "Field Sales Executive";
  const preferredLocation = formData.location?.trim() || formData["preferred-location"]?.trim() || "Jaipur";
  const experience = formData.experience?.trim() || formData["total-experience"]?.trim() || "Fresher / Entry Level";
  const notice = formData.noticePeriod?.trim() || formData["notice-period"]?.trim() || "Immediate Joining";
  const rawCoverNote = formData.message?.trim() || formData["cover-note-why-ashlar"]?.trim() || "";

  // Prepend preferred location so recruitment desk always sees candidate's chosen location
  const coverNote = preferredLocation
    ? `[Preferred Location: ${preferredLocation}]\n${rawCoverNote}`.trim()
    : rawCoverNote;

  // Payload strictly matching crm.muftlo.in form-ubhu4lxoq schema
  const payload = {
    "full-name": candidateName,
    "email-address": email,
    "phone-number": numericPhone,
    "current-company-college": currentOrg,
    "position-applied-for": role,
    "preferred-location": preferredLocation,
    location: preferredLocation,
    "total-experience": experience,
    "notice-period": notice,
    "resume-link-linkedin-url": resumeUrl,
    "cover-note-why-ashlar": coverNote,
  };

  const res = await fetch(CRM_CAREER_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...getTenantHeaders(),
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    const errorMsg = errorData?.error || errorData?.message || "Failed to submit application. Please try again.";
    throw new Error(errorMsg);
  }

  return res.json();
};



